import React, { useEffect, useState, useRef } from 'react';
import { regionForCountry } from '../../../lib/findBobUtils';
import { useSuccessionTheme } from '../SuccessionThemeProvider';
import { useIntl } from 'react-intl';
import { useCurrency } from '../../../hooks/useCurrency';

// Define province/state/region data outside of components so it's accessible throughout the file.
// UK regions — 8 SJP canonical regions + "Other UK" fallback (see config/uk_postcode_regions.yml).
// user.province is populated with one of these values by User#geocode_me for GB tenants.
const ukSjpRegions = [
  { code: '',           name: 'Any Region',  value: '' },
  { code: 'LON',        name: 'London',      value: 'London' },
  { code: 'SE',         name: 'South East',  value: 'South East' },
  { code: 'SW',         name: 'South West',  value: 'South West' },
  { code: 'MID',        name: 'Midlands',    value: 'Midlands' },
  { code: 'N',          name: 'North',       value: 'North' },
  { code: 'SCO',        name: 'Scotland',    value: 'Scotland' },
  { code: 'WAL',        name: 'Wales',       value: 'Wales' },
  { code: 'NI',         name: 'NI',          value: 'NI' },
  { code: 'OTHER_UK',   name: 'Other UK',    value: 'Other UK' },
];

// Canadian provinces
const canadianProvinces = [
  { code: '', name: 'Any Province', nameFr: 'Toute province', value: '' },
  { code: 'AB', name: 'Alberta', nameFr: 'Alberta', value: 'Alberta' },
  { code: 'BC', name: 'British Columbia', nameFr: 'Colombie-Britannique', value: 'British Columbia' },
  { code: 'MB', name: 'Manitoba', nameFr: 'Manitoba', value: 'Manitoba' },
  { code: 'NB', name: 'New Brunswick', nameFr: 'Nouveau-Brunswick', value: 'New Brunswick' },
  { code: 'NL', name: 'Newfoundland and Labrador', nameFr: 'Terre-Neuve-et-Labrador', value: 'Newfoundland and Labrador' },
  { code: 'NT', name: 'Northwest Territories', nameFr: 'Territoires du Nord-Ouest', value: 'Northwest Territories' },
  { code: 'NS', name: 'Nova Scotia', nameFr: 'Nouvelle-Écosse', value: 'Nova Scotia' },
  { code: 'NU', name: 'Nunavut', nameFr: 'Nunavut', value: 'Nunavut' },
  { code: 'ON', name: 'Ontario', nameFr: 'Ontario', value: 'Ontario' },
  { code: 'PE', name: 'Prince Edward Island', nameFr: 'Île-du-Prince-Édouard', value: 'Prince Edward Island' },
  { code: 'QC', name: 'Quebec', nameFr: 'Québec', value: 'Quebec' },
  { code: 'SK', name: 'Saskatchewan', nameFr: 'Saskatchewan', value: 'Saskatchewan' },
  { code: 'YT', name: 'Yukon', nameFr: 'Yukon', value: 'Yukon' },
];

// US states
const usStates = [
  { code: '', name: 'Any State', value: '' },
  { code: 'AL', name: 'Alabama', value: 'Alabama' },
  { code: 'AK', name: 'Alaska', value: 'Alaska' },
  { code: 'AZ', name: 'Arizona', value: 'Arizona' },
  { code: 'AR', name: 'Arkansas', value: 'Arkansas' },
  { code: 'CA', name: 'California', value: 'California' },
  { code: 'CO', name: 'Colorado', value: 'Colorado' },
  { code: 'CT', name: 'Connecticut', value: 'Connecticut' },
  { code: 'DE', name: 'Delaware', value: 'Delaware' },
  { code: 'FL', name: 'Florida', value: 'Florida' },
  { code: 'GA', name: 'Georgia', value: 'Georgia' },
  { code: 'HI', name: 'Hawaii', value: 'Hawaii' },
  { code: 'ID', name: 'Idaho', value: 'Idaho' },
  { code: 'IL', name: 'Illinois', value: 'Illinois' },
  { code: 'IN', name: 'Indiana', value: 'Indiana' },
  { code: 'IA', name: 'Iowa', value: 'Iowa' },
  { code: 'KS', name: 'Kansas', value: 'Kansas' },
  { code: 'KY', name: 'Kentucky', value: 'Kentucky' },
  { code: 'LA', name: 'Louisiana', value: 'Louisiana' },
  { code: 'ME', name: 'Maine', value: 'Maine' },
  { code: 'MD', name: 'Maryland', value: 'Maryland' },
  { code: 'MA', name: 'Massachusetts', value: 'Massachusetts' },
  { code: 'MI', name: 'Michigan', value: 'Michigan' },
  { code: 'MN', name: 'Minnesota', value: 'Minnesota' },
  { code: 'MS', name: 'Mississippi', value: 'Mississippi' },
  { code: 'MO', name: 'Missouri', value: 'Missouri' },
  { code: 'MT', name: 'Montana', value: 'Montana' },
  { code: 'NE', name: 'Nebraska', value: 'Nebraska' },
  { code: 'NV', name: 'Nevada', value: 'Nevada' },
  { code: 'NH', name: 'New Hampshire', value: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey', value: 'New Jersey' },
  { code: 'NM', name: 'New Mexico', value: 'New Mexico' },
  { code: 'NY', name: 'New York', value: 'New York' },
  { code: 'NC', name: 'North Carolina', value: 'North Carolina' },
  { code: 'ND', name: 'North Dakota', value: 'North Dakota' },
  { code: 'OH', name: 'Ohio', value: 'Ohio' },
  { code: 'OK', name: 'Oklahoma', value: 'Oklahoma' },
  { code: 'OR', name: 'Oregon', value: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania', value: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island', value: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina', value: 'South Carolina' },
  { code: 'SD', name: 'South Dakota', value: 'South Dakota' },
  { code: 'TN', name: 'Tennessee', value: 'Tennessee' },
  { code: 'TX', name: 'Texas', value: 'Texas' },
  { code: 'UT', name: 'Utah', value: 'Utah' },
  { code: 'VT', name: 'Vermont', value: 'Vermont' },
  { code: 'VA', name: 'Virginia', value: 'Virginia' },
  { code: 'WA', name: 'Washington', value: 'Washington' },
  { code: 'WV', name: 'West Virginia', value: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin', value: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming', value: 'Wyoming' },
  { code: 'DC', name: 'District of Columbia', value: 'District of Columbia' }
];

// Search dropdown component with enhanced styling
const SearchDropdown = ({ label, value, onChange, options, placeholder, multiple = false }) => {
  return (
    <div className="mb-4">
      {label && <label className="block text-sm font-medium text-succession-text-primary mb-1 transition-colors duration-300">{label}</label>}
      <div className="relative">
        <select
          className="w-full border border-succession-border rounded-md px-3 py-2 text-sm appearance-none bg-succession-bg-primary text-succession-text-primary focus:outline-none focus:ring-succession-primary focus:border-succession-primary transition-colors duration-300"
          value={value || ''}
          onChange={onChange}
          multiple={multiple}
        >
          <option value="">{placeholder || label}</option>
          {options.map(option => (
            <option key={option.value || option.id || option} value={option.value || option.id || option}>
              {option.label || option.name || option}
            </option>
          ))}
        </select>
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-succession-text-muted transition-colors duration-300">
          <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"></path>
          </svg>
        </div>
      </div>
    </div>
  );
};

// Multi-select component with enhanced styling
const SearchMultiSelect = ({ label, values, onChange, options }) => {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Filter options based on search term
  const filteredOptions = searchTerm 
    ? options.filter(option => {
        const label = option.label || option.name || option;
        return label.toLowerCase().includes(searchTerm.toLowerCase());
      })
    : options;
  
  // Get selected values as array of labels for display
  const selectedLabels = options
    .filter(option => {
      const optionValue = option.value || option.id || option;
      return Array.isArray(values) && values.includes(optionValue);
    })
    .map(option => option.label || option.name || option);
  
  // Toggle an option selection
  const toggleOption = (optionValue) => {
    const isSelected = Array.isArray(values) && values.includes(optionValue);
    const newValues = isSelected
      ? values.filter(v => v !== optionValue)
      : [...(values || []), optionValue];
    onChange(newValues);
  };
  
  // Clear all selections
  const clearSelections = (e) => {
    e.stopPropagation();
    onChange([]);
  };
  
  return (
    <div className="mb-3">
      <label className="block text-sm font-medium text-succession-text-primary mb-1 transition-colors duration-300">{label}</label>
      
      {/* Dropdown button */}
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          className={`w-full px-3 py-2 text-left bg-succession-bg-primary border transition-all duration-200 rounded-md shadow-sm focus:outline-none ${isOpen ? 'border-succession-primary ring-1 ring-succession-primary' : 'border-succession-border'}`}
          onClick={() => setIsOpen(!isOpen)}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          <div className="flex justify-between items-center">
            <div className="flex-grow truncate mr-2 text-sm">
              {values && values.length > 0 ? (
                <span className="text-succession-text-primary transition-colors duration-300">
                  {selectedLabels.length > 2 
                    ? `${selectedLabels.slice(0, 2).join(', ')} +${selectedLabels.length - 2} more`
                    : selectedLabels.join(', ')}
                </span>
              ) : (
                <span className="text-succession-text-muted transition-colors duration-300">{intl.formatMessage({ id: 'succession.search.sidebar.select_placeholder' }, { label: label.toLowerCase() })}</span>
              )}
            </div>
            <div className="flex items-center">
              {values && values.length > 0 && (
                <button
                  type="button"
                  onClick={clearSelections}
                  className="text-succession-text-muted hover:text-succession-text-secondary mr-1.5 transition-colors duration-300"
                  aria-label={intl.formatMessage({ id: 'succession.search.sidebar.clear_selection' })}
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
              <svg 
                className={`h-5 w-5 text-succession-text-muted transition-all duration-300 ${isOpen ? 'transform rotate-180' : ''}`}
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </button>
        
        {/* Dropdown panel */}
        {isOpen && (
          <div className="absolute z-10 w-full mt-1 bg-succession-bg-primary rounded-md shadow-succession-lg border border-succession-border overflow-hidden transition-theme">
            {/* Search input - improved version */}
            <div className="p-1.5 border-b border-succession-border">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
                  <svg className="h-3.5 w-3.5 text-succession-text-muted transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="search"
                  className="block w-full pl-8 pr-3 py-1 bg-succession-bg-secondary border border-succession-border rounded-md text-sm text-succession-text-primary focus:outline-none focus:ring-1 focus:ring-succession-primary focus:border-succession-primary transition-colors duration-300"
                  placeholder={intl.formatMessage({ id: 'succession.search.sidebar.search_placeholder' }, { label: label.toLowerCase() })}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            
            {/* Options list */}
            <div className="max-h-60 overflow-y-auto bg-succession-bg-secondary">
              {filteredOptions.length > 0 ? (
                filteredOptions.map(option => {
                  const optionValue = option.value || option.id || option;
                  const optionLabel = option.label || option.name || option;
                  const isSelected = Array.isArray(values) && values.includes(optionValue);
                  
                  return (
                    <div
                      key={optionValue}
                      className={`flex items-center px-3 py-1.5 text-sm cursor-pointer transition-colors duration-300 ${
                        isSelected 
                          ? 'bg-succession-primary-10 text-succession-primary' 
                          : 'text-succession-text-primary hover:bg-succession-bg-secondary'
                      }`}
                      onClick={() => toggleOption(optionValue)}
                    >
                      <div className={`flex-shrink-0 h-4 w-4 mr-2 flex items-center justify-center border rounded transition-colors duration-300 ${
                        isSelected 
                          ? 'bg-succession-primary border-succession-primary' 
                          : 'border-succession-border'
                      }`}>
                        {isSelected && (
                          <svg className="h-3 w-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                      <span className="truncate">{optionLabel}</span>
                    </div>
                  );
                })
              ) : (
                <div className="px-3 py-2 text-sm text-succession-text-muted text-center transition-colors duration-300">{intl.formatMessage({ id: 'succession.search.sidebar.no_options_found' })}</div>
              )}
            </div>
            
            {/* Controls footer */}
            <div className="py-1 px-2 bg-succession-bg-secondary border-t border-succession-border flex justify-between items-center text-xs transition-colors duration-300">
              <div className="text-succession-text-muted transition-colors duration-300">
                {intl.formatMessage({ id: 'succession.search.sidebar.selected_count' }, { count: values && values.length })}
              </div>
              <div className="flex space-x-2">
                <button
                  type="button"
                  onClick={clearSelections}
                  className="px-2 py-0.5 text-xs font-medium text-succession-text-secondary hover:text-succession-text-primary transition-colors duration-300"
                >
                  {intl.formatMessage({ id: 'succession.search.sidebar.clear_all' })}
                </button>
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="px-2 py-0.5 text-xs font-medium text-succession-primary hover:text-succession-primary-hover transition-colors duration-300"
                >
                  {intl.formatMessage({ id: 'succession.search.sidebar.done' })}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const SearchSidebar = ({ 
  searchParams, 
  onSearchChange, 
  onSearchSubmit, 
  onAIModeChange,
  onResetConversation,
  conversationHistory = [],
  isLoading = false,
  data,
  countryCode,
  company,
  searchInputValue,
  setSearchInputValue,
  onKeyPress
}) => {
  // Use succession theme context
  const intl = useIntl();
  const { theme, isLightTheme, isDarkTheme } = useSuccessionTheme();
  const { symbol } = useCurrency();
  
  // State for collapsible sections and conversational history
  const [showFinancialCriteria, setShowFinancialCriteria] = useState(false);
  const [showPracticeDetails, setShowPracticeDetails] = useState(false);
  const conversationRef = useRef(null);
  
  // Check if AI search is a supported feature
  const hasAISearchFeature = data?.company?.features?.includes('ai_search');
  
  // Ensure AI mode is on by default when component loads if the feature is available
  useEffect(() => {
    if (hasAISearchFeature && (searchParams.aiMode === undefined || searchParams.aiMode === null)) {
      onAIModeChange(true);
    } else if (!hasAISearchFeature && searchParams.aiMode) {
      // If AI search feature isn't available but AI mode is on, turn it off
      onAIModeChange(false);
    }
  }, [searchParams.aiMode, hasAISearchFeature]);
  
  // Scroll to bottom of conversation whenever it updates
  useEffect(() => {
    if (conversationRef.current) {
      conversationRef.current.scrollTop = conversationRef.current.scrollHeight;
    }
  }, [conversationHistory]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // When submitting, set the searchParams.prompt value to the current input value
    if (searchParams.aiMode && searchInputValue) {
      onSearchChange('prompt', searchInputValue);
      // Use setTimeout to ensure state is updated before submitting
      setTimeout(() => {
        onSearchSubmit();
      }, 0);
    } else {
      onSearchSubmit();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      
      // Same as handleSubmit - update prompt before submitting
      if (searchParams.aiMode && searchInputValue) {
        onSearchChange('prompt', searchInputValue);
        // Use setTimeout to ensure state is updated before submitting
        setTimeout(() => {
          onSearchSubmit();
        }, 0);
      } else {
        onSearchSubmit();
      }
    }
  };

  const handleAIToggle = () => {
    onAIModeChange(!searchParams.aiMode);
  };

  const renderRangeFields = (title, minField, maxField) => {
    return (
      <div className="mb-4">
        <label className="block text-sm font-medium text-succession-text-primary mb-2 transition-colors duration-300">{title}</label>
        <div className="flex flex-col space-y-2">
          {/* Min input field with improved styling */}
          <div className="bg-succession-bg-secondary border border-succession-border rounded-md flex transition-colors duration-300">
            <div className="flex items-center justify-center w-10 h-10 bg-succession-bg-tertiary border-r border-succession-border transition-colors duration-300">
              <span className="text-succession-text-muted text-sm transition-colors duration-300">{symbol}</span>
            </div>
            <input
              type="number"
              placeholder={intl.formatMessage({ id: 'succession.search.sidebar.range_min' })}
              value={searchParams[minField] || ''}
              onChange={(e) => onSearchChange(minField, e.target.value ? Number(e.target.value) : null)}
              className="flex-1 bg-transparent py-2 px-3 text-sm focus:outline-none text-succession-text-primary transition-colors duration-300"
            />
            <div className="flex items-center justify-center w-10 h-10 bg-succession-bg-tertiary border-l border-succession-border transition-colors duration-300">
              <span className="text-succession-text-muted text-sm transition-colors duration-300">{intl.formatMessage({ id: 'succession.search.sidebar.range_min' })}</span>
            </div>
          </div>

          {/* Max input field with improved styling */}
          <div className="bg-succession-bg-secondary border border-succession-border rounded-md flex transition-colors duration-300">
            <div className="flex items-center justify-center w-10 h-10 bg-succession-bg-tertiary border-r border-succession-border transition-colors duration-300">
              <span className="text-succession-text-muted text-sm transition-colors duration-300">{symbol}</span>
            </div>
            <input
              type="number"
              placeholder={intl.formatMessage({ id: 'succession.search.sidebar.range_max' })}
              value={searchParams[maxField] || ''}
              onChange={(e) => onSearchChange(maxField, e.target.value ? Number(e.target.value) : null)}
              className="flex-1 bg-transparent py-2 px-3 text-sm focus:outline-none text-succession-text-primary transition-colors duration-300"
            />
            <div className="flex items-center justify-center w-10 h-10 bg-succession-bg-tertiary border-l border-succession-border transition-colors duration-300">
              <span className="text-succession-text-muted text-sm transition-colors duration-300">{intl.formatMessage({ id: 'succession.search.sidebar.range_max' })}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleTransitionGoalChange = (e) => {
    const value = e.target.value;
    const isChecked = e.target.checked;
    
    // Update the transitionGoalsName array based on checkbox state
    onSearchChange('transitionGoalsName', 
      isChecked
        ? [...(searchParams.transitionGoalsName || []), value]
        : (searchParams.transitionGoalsName || []).filter(goal => goal !== value)
    );
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    
    try {
      const date = new Date(timestamp);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (e) {
      return '';
    }
  };
  
  // Format conversation history for display
  const renderConversationHistory = () => {
    if (!conversationHistory || conversationHistory.length === 0) return null;
    
    return (
      <div 
        ref={conversationRef}
        className="max-h-64 overflow-y-auto border border-succession-border rounded-md mb-4 p-2 bg-succession-bg-secondary"
      >
        {conversationHistory.map((item, index) => (
          <div 
            key={index} 
            className={`mb-3 p-3 rounded-lg ${
              item.role === 'user' 
                ? 'bg-succession-primary-10 border border-succession-primary-light ml-8 shadow-sm' 
                : 'bg-succession-bg-primary border border-succession-border mr-8 shadow-sm'
            }`}
          >
            <div className="text-sm">
              <span className={`font-medium ${item.role === 'user' ? 'text-succession-primary' : 'text-succession-text-primary'}`}>
                {item.role === 'user' ? intl.formatMessage({ id: 'succession.search.sidebar.you_prefix' }) : intl.formatMessage({ id: 'succession.search.sidebar.ai_prefix' })}
              </span>
              {item.content}
            </div>
            {item.timestamp && (
              <div className="text-xs text-succession-text-muted mt-1">
                {formatTimestamp(item.timestamp)}
              </div>
            )}
          </div>
        ))}
        {isLoading && (
          <div className="bg-succession-primary-10 border border-succession-primary-light ml-8 p-3 rounded-lg shadow-sm">
            <div className="flex items-center">
              <span className="font-medium text-sm text-succession-primary">{intl.formatMessage({ id: 'succession.search.sidebar.ai_prefix' })}</span>
              <span className="ml-1 text-sm loading-dots">
                {intl.formatMessage({ id: 'succession.search.sidebar.thinking' })}
                <span className="dot dot1">.</span>
                <span className="dot dot2">.</span>
                <span className="dot dot3">.</span>
              </span>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render AI Search Interface with improved styling
  const renderAISearch = () => {
    return (
      <div>
        {renderConversationHistory()}
        
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-succession-text-primary transition-colors duration-300">{intl.formatMessage({ id: 'succession.search.sidebar.ai_powered_search' })}</h3>
          </div>
          
          <div className="flex items-center justify-between bg-succession-bg-tertiary p-2 rounded-md mb-3 transition-colors duration-300">
            <div className="flex items-center">
              <svg className="h-4 w-4 text-succession-primary mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
              </svg>
              <span className="text-xs font-medium text-succession-text-primary">{intl.formatMessage({ id: 'succession.search.sidebar.ai_powered_search' })}</span>
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-succession-primary-light text-succession-primary-dark">
                {intl.formatMessage({ id: 'succession.search.sidebar.ai_mode_enabled' })}
              </span>
            </div>
            <button
              type="button"
              onClick={handleAIToggle}
              className="relative inline-flex h-5 w-10 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-succession-primary transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-succession-primary focus:ring-offset-2"
              role="switch"
              aria-checked="true"
            >
              <span className="sr-only">{intl.formatMessage({ id: 'succession.search.sidebar.toggle_ai_search' })}</span>
              <span aria-hidden="true" className="pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out translate-x-5"></span>
            </button>
          </div>
          
          <textarea
            className="w-full border border-succession-border rounded-md p-3 focus:outline-none focus:ring-2 focus:ring-succession-primary focus:border-transparent transition-colors duration-300"
            placeholder={intl.formatMessage({ id: 'succession.search.sidebar.ai_search_placeholder' })}
            value={searchInputValue || ''}
            onChange={(e) => setSearchInputValue(e.target.value)}
            onKeyPress={onKeyPress}
            rows="3"
          ></textarea>
        </div>
        
        <div className="flex space-x-2 mb-6">
          <button
            type="button"
            onClick={handleSubmit}
            className="flex-1 bg-succession-primary text-white py-2 px-4 rounded-md hover:bg-succession-primary-dark focus:outline-none focus:ring-2 focus:ring-succession-primary focus:ring-offset-2 transition-colors shadow-sm"
            disabled={isLoading || !searchInputValue}
          >
            {isLoading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {intl.formatMessage({ id: 'succession.search.sidebar.searching' })}
              </span>
            ) : intl.formatMessage({ id: 'succession.search.sidebar.search' })}
          </button>

          <button
            type="button"
            onClick={() => {
              if (onResetConversation) {
                onResetConversation();
              } else {
                setSearchInputValue('');
              }
            }}
            className="flex-1 bg-succession-bg-tertiary text-succession-text-primary py-2 px-4 rounded-md hover:bg-succession-bg-secondary focus:outline-none focus:ring-2 focus:ring-succession-border focus:ring-offset-2 transition-colors shadow-sm"
          >
            {intl.formatMessage({ id: 'succession.search.sidebar.clear' })}
          </button>
        </div>

        <button
          type="button"
          onClick={() => {
            onAIModeChange(false);
            onSearchChange('aiMode', false);
          }}
          className="w-full text-succession-text-primary py-2 px-4 bg-succession-bg-tertiary rounded-md hover:bg-succession-bg-secondary focus:outline-none border border-succession-border transition-colors"
        >
          {intl.formatMessage({ id: 'succession.search.sidebar.switch_to_manual' })}
        </button>
      </div>
    );
  };

  // Traditional search form
  const renderTraditionalSearch = () => {
    const isUSCompany = countryCode?.toUpperCase() === 'US';
    const isGBCompany = countryCode?.toUpperCase() === 'GB';
    const regionOptions = (() => {
      if (isUSCompany) return usStates;
      if (isGBCompany) return ukSjpRegions;
      return canadianProvinces.map(p => ({ ...p, name: intl.locale === 'fr' ? p.nameFr : p.name }));
    })();
    const regionLabelKey = isUSCompany
      ? 'succession.search.sidebar.state_label'
      : (isGBCompany ? 'succession.search.sidebar.region_label' : 'succession.search.sidebar.province_label');
    const regionPlaceholderKey = isUSCompany
      ? 'succession.search.sidebar.select_state'
      : (isGBCompany ? 'succession.search.sidebar.select_region' : 'succession.search.sidebar.select_province');
    const hasPartialSalesFeature = data?.company?.features?.includes('partial_sales');
    const hasStudentFeature = data?.company?.features?.includes('students');
    const hasAmpTraineeFeature = data?.company?.features?.includes('search_amp_trainee');
    const hasDiscretionaryInvestmentModelsFeature = data?.company?.features?.includes('discretionary_investment_models');
    
    return (
      <div className="space-y-4">
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-succession-text-primary mb-2 transition-colors duration-300">{intl.formatMessage({ id: 'succession.search.sidebar.manual_search' })}</h3>

          {hasAISearchFeature && (
            <div className="flex items-center justify-between bg-succession-bg-tertiary p-2 rounded-md mb-3 transition-colors duration-300">
              <div className="flex items-center">
                <svg className="h-4 w-4 text-succession-text-muted transition-colors duration-300" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                </svg>
                <span className="text-xs font-medium text-succession-text-muted transition-colors duration-300">{intl.formatMessage({ id: 'succession.search.sidebar.ai_powered_search' })}</span>
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-succession-bg-tertiary text-succession-text-primary transition-colors duration-300">
                  {intl.formatMessage({ id: 'succession.search.sidebar.ai_mode_disabled' })}
                </span>
              </div>
              <button
                type="button"
                onClick={handleAIToggle}
                className="relative inline-flex h-5 w-10 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-succession-bg-tertiary transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-succession-primary focus:ring-offset-2"
                role="switch"
                aria-checked="false"
              >
                <span className="sr-only">{intl.formatMessage({ id: 'succession.search.sidebar.toggle_ai_search' })}</span>
                <span aria-hidden="true" className="pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out translate-x-0"></span>
              </button>
            </div>
          )}
        </div>

        {/* Practice ID */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-succession-text-primary mb-1 transition-colors duration-300">{intl.formatMessage({ id: 'succession.search.sidebar.practice_id_label' })}</label>
          <input
            type="text"
            placeholder={intl.formatMessage({ id: 'succession.search.sidebar.practice_id_placeholder' })}
            className="w-full border border-succession-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-succession-primary focus:border-succession-primary transition-colors duration-300"
            value={searchParams.userName || ''}
            onChange={(e) => onSearchChange('userName', e.target.value)}
          />
        </div>

        {/* Transition Goals / Interested In */}
        <SearchDropdown
          label={intl.formatMessage({ id: 'succession.search.sidebar.interested_in_label' })}
          value={searchParams.transitionGoalsName ? searchParams.transitionGoalsName[0] : ''}
          onChange={(e) => {
            if (e.target.value) {
              onSearchChange('transitionGoalsName', [e.target.value]);
            } else {
              onSearchChange('transitionGoalsName', null);
            }
          }}
          options={(data?.getCurrentUser?.searchable_transition_goals || []).map(goal => {
            const GOAL_KEY_MAP = {
              'Buying': 'buying',
              'Selling': 'selling',
              'Selling my practice': 'selling_my_practice',
              'Selling part of my practice': 'selling_part_of_my_practice',
              'Find a Successor': 'find_a_successor',
              'Become a Successor': 'become_a_successor',
              'Merging': 'merging',
              'Partnering': 'partnering',
              'Forming a Partnership': 'forming_a_partnership',
              'Finding a Junior Partner': 'finding_a_junior_partner',
              'Becoming a Junior Partner': 'becoming_a_junior_partner',
              'Business Continuity': 'business_continuity',
              'Mentoring': 'mentoring',
              'Being Mentored': 'being_mentored',
              'Looking for Successor': 'looking_for_successor',
            };
            const key = GOAL_KEY_MAP[goal];
            const label = key ? intl.formatMessage({ id: `transition_goals.${key}`, defaultMessage: goal }) : goal;
            return { value: goal, label };
          })}
          placeholder={intl.formatMessage({ id: 'succession.search.sidebar.interested_in_placeholder' })}
        />

        {/* Planning Preference */}
        <SearchDropdown
          label={intl.formatMessage({ id: 'succession.search.sidebar.planning_preference_label' })}
          value={searchParams.planningPreference || ''}
          onChange={(e) => onSearchChange('planningPreference', e.target.value)}
          options={[
            { value: 'find_partner', label: intl.formatMessage({ id: 'succession.search.sidebar.planning_find_partner' }) },
            { value: 'become_partner', label: intl.formatMessage({ id: 'succession.search.sidebar.planning_become_partner' }) },
            { value: 'already_have', label: intl.formatMessage({ id: 'succession.search.sidebar.planning_already_have' }) },
            { value: 'no_plan_preference', label: intl.formatMessage({ id: 'succession.search.sidebar.planning_no_preference' }) }
          ]}
          placeholder={intl.formatMessage({ id: 'succession.search.sidebar.planning_preference_placeholder' })}
        />

        {/* Miles from me (Location Within) */}
        <SearchDropdown
          label={intl.formatMessage({ id: 'succession.search.sidebar.distance_label' })}
          value={searchParams.locationWithin || ''}
          onChange={(e) => onSearchChange('locationWithin', e.target.value)}
          options={[
            { value: '50', label: '50 km' },
            { value: '100', label: '100 km' },
            { value: '200', label: '200 km' },
            { value: '500', label: '500 km' },
            { value: '1000', label: '1000 km' },
            { value: '5000', label: intl.formatMessage({ id: 'succession.search.sidebar.distance_everywhere' }) }
          ]}
          placeholder={intl.formatMessage({ id: 'succession.search.sidebar.distance_placeholder' })}
        />

        {/* Province/State/Region based on country */}
        <SearchDropdown
          label={intl.formatMessage({ id: regionLabelKey, defaultMessage: isGBCompany ? 'Region' : 'Province' })}
          value={searchParams.province || ''}
          onChange={(e) => onSearchChange('province', e.target.value)}
          options={regionOptions}
          placeholder={intl.formatMessage({ id: regionPlaceholderKey, defaultMessage: isGBCompany ? 'Select Region' : 'Select Province' })}
        />

        {/* Has Partial Sales */}
        {hasPartialSalesFeature && (
          <SearchDropdown
            label={intl.formatMessage({ id: 'succession.search.sidebar.has_partial_sales_label' })}
            value={searchParams.hasPartialSales === true ? 'yes' : searchParams.hasPartialSales === false ? 'no' : ''}
            onChange={(e) => {
              const value = e.target.value;
              if (value === 'yes') {
                onSearchChange('hasPartialSales', true);
              } else if (value === 'no') {
                onSearchChange('hasPartialSales', false);
              } else {
                onSearchChange('hasPartialSales', null);
              }
            }}
            options={[
              { value: 'yes', label: 'Yes' },
              { value: 'no', label: 'No' }
            ]}
            placeholder={intl.formatMessage({ id: 'succession.search.sidebar.select_option' })}
          />
        )}

        {/* Partial Sale Locations - Only show when user has selected Yes for partial sales */}
        {hasPartialSalesFeature && searchParams.hasPartialSales === true && (
          <SearchMultiSelect
            label={intl.formatMessage({ id: 'succession.search.sidebar.partial_sale_locations_label' })}
            values={searchParams.partialSaleLocations || []}
            onChange={(selectedLocations) => onSearchChange('partialSaleLocations', selectedLocations)}
            options={regionOptions
              .filter(location => location.value) // Exclude "Any State/Province/Region" option
              .map(location => ({
                value: location.value,
                label: intl.locale === 'fr' ? (location.nameFr || location.name) : location.name
              }))
            }
          />
        )}
        
        {/* Financial Criteria Dropdown */}
        <div 
          className="bg-succession-bg-tertiary border border-succession-border rounded-md px-4 py-3 flex justify-between items-center cursor-pointer transition-colors hover:bg-succession-bg-secondary" 
          onClick={() => setShowFinancialCriteria(!showFinancialCriteria)}
        >
          <span className="text-sm font-medium text-succession-text-primary transition-colors duration-300">{intl.formatMessage({ id: 'succession.search.sidebar.financial_criteria' })}</span>
          <svg className={`h-5 w-5 text-succession-text-muted transition-transform ${showFinancialCriteria ? 'rotate-180' : ''}`} 
              fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"></path>
          </svg>
        </div>
        
        {showFinancialCriteria && (
          <div className="pl-3 pr-3 pt-4 pb-3 border border-succession-border rounded-md mb-4 mt-2 space-y-4 bg-succession-bg-primary shadow-sm">
            {/* Annual Revenue */}
            {renderRangeFields(intl.formatMessage({ id: 'succession.search.sidebar.annual_revenue' }), 'annualRevenueGt', 'annualRevenueLt')}

            {/* Gross Production */}
            {renderRangeFields(intl.formatMessage({ id: 'succession.search.sidebar.gross_production' }), 'grossProductionGt', 'grossProductionLt')}

            {/* Assets Under Management */}
            {renderRangeFields(intl.formatMessage({ id: 'succession.search.sidebar.assets_under_management' }), 'assetsUnderManagementGt', 'assetsUnderManagementLt')}
          </div>
        )}
        
        {/* Practice Details Dropdown */}
        <div 
          className="bg-succession-bg-tertiary border border-succession-border rounded-md px-4 py-3 flex justify-between items-center cursor-pointer transition-colors hover:bg-succession-bg-secondary"
          onClick={() => setShowPracticeDetails(!showPracticeDetails)}
        >
          <span className="text-sm font-medium text-succession-text-primary transition-colors duration-300">{intl.formatMessage({ id: 'succession.search.sidebar.practice_details' })}</span>
          <svg className={`h-5 w-5 text-succession-text-muted transition-transform ${showPracticeDetails ? 'rotate-180' : ''}`} 
              fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"></path>
          </svg>
        </div>
        
        {showPracticeDetails && (
          <div className="pl-3 pr-3 pt-4 pb-3 border border-succession-border rounded-md mb-4 mt-2 space-y-4 bg-succession-bg-primary shadow-sm">
            {/* Affiliations */}
            {data?.availableAffiliations?.length > 0 && (
              <SearchMultiSelect
                label={intl.formatMessage({ id: 'succession.search.sidebar.affiliations_label' })}
                values={searchParams.affiliations || []}
                onChange={(values) => onSearchChange('affiliations', values)}
                options={data.availableAffiliations}
              />
            )}
            
            {/* Designations */}
            {data?.availableDesignations?.length > 0 && (
              <SearchMultiSelect
                label={intl.formatMessage({ id: 'succession.search.sidebar.designations_label' })}
                values={searchParams.designations || []}
                onChange={(values) => onSearchChange('designations', values)}
                options={data.availableDesignations}
              />
            )}
            
            {/* Languages */}
            {data?.company?.languages?.length > 0 && (
              <SearchMultiSelect
                label={intl.formatMessage({ id: 'succession.search.sidebar.languages_label' })}
                values={searchParams.languages || []}
                onChange={(values) => onSearchChange('languages', values)}
                options={(data.company.languages || []).map(lang => ({
                  id: lang.name,
                  name: lang.name
                }))}
              />
            )}
            
            {/* Primary Markets */}
            {data?.company?.available_primary_markets?.length > 0 && (
              <SearchMultiSelect
                label={intl.formatMessage({ id: 'succession.search.sidebar.primary_markets_label' })}
                values={searchParams.primaryMarkets || []}
                onChange={(values) => onSearchChange('primaryMarkets', values)}
                options={(data.company.available_primary_markets || []).map(market => ({
                  id: market,
                  name: market.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
                }))}
              />
            )}
            
            {/* Feature-flagged options */}
            <div className="space-y-3 pt-2">
              {/* Discretionary Investment Models */}
              {hasDiscretionaryInvestmentModelsFeature && (
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="discretionaryInvestmentModels"
                    checked={searchParams.discretionaryInvestmentModels || false}
                    onChange={(e) => onSearchChange('discretionaryInvestmentModels', e.target.checked)}
                    className="h-4 w-4 text-succession-primary border-succession-border rounded focus:ring-succession-primary"
                  />
                  <label htmlFor="discretionaryInvestmentModels" className="ml-2 block text-sm text-succession-text-primary">
                    {intl.formatMessage({ id: 'succession.search.sidebar.discretionary_investment_models' })}
                  </label>
                </div>
              )}        
              
              {/* Is Student */}
              {hasStudentFeature && (
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isStudent"
                    checked={searchParams.isStudent || false}
                    onChange={(e) => onSearchChange('isStudent', e.target.checked)}
                    className="h-4 w-4 text-succession-primary border-succession-border rounded focus:ring-succession-primary"
                  />
                  <label htmlFor="isStudent" className="ml-2 block text-sm text-succession-text-primary">
                    {intl.formatMessage({ id: 'succession.search.sidebar.is_student' })}
                  </label>
                </div>
              )}

              {/* AMP Trainee — gated on the search_amp_trainee company feature (RJ/ICD) */}
              {hasAmpTraineeFeature && (
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="ampTraineeStatus"
                    checked={searchParams.ampTraineeStatus || false}
                    // Tri-state: checked => true (only AMP Trainees); unchecked => null
                    // (no filter / full list). Sending `false` would filter to
                    // amp_trainee_status=false and drop everyone whose value is nil.
                    onChange={(e) => onSearchChange('ampTraineeStatus', e.target.checked ? true : null)}
                    className="h-4 w-4 text-succession-primary border-succession-border rounded focus:ring-succession-primary"
                  />
                  <label htmlFor="ampTraineeStatus" className="ml-2 block text-sm text-succession-text-primary">
                    {intl.formatMessage({ id: 'succession.search.sidebar.amp_trainee' })}
                  </label>
                </div>
              )}
            </div>
          </div>
        )}
        
        <div className="flex space-x-2 mt-6">
          <button
            type="button"
            onClick={handleSubmit}
            className="flex-1 bg-succession-primary text-white py-2 px-4 rounded-md hover:bg-succession-primary-dark focus:outline-none focus:ring-2 focus:ring-succession-primary focus:ring-offset-2 transition-colors shadow-sm"
            disabled={isLoading}
          >
            {isLoading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {intl.formatMessage({ id: 'succession.search.sidebar.searching' })}
              </span>
            ) : intl.formatMessage({ id: 'succession.search.sidebar.search' })}
          </button>

          <button
            type="button"
            onClick={() => {
              // Reset all search parameters
              const resetParams = {
                page: 1,
                perPage: searchParams.perPage || 20,
                aiMode: searchParams.aiMode
              };

              // Clear each parameter in state
              Object.keys(searchParams).forEach(key => {
                if (key !== 'page' && key !== 'perPage' && key !== 'aiMode') {
                  onSearchChange(key, null);
                }
              });

              // Submit with reset parameters immediately
              onSearchSubmit(resetParams);
            }}
            className="flex-1 bg-succession-bg-tertiary text-succession-text-primary py-2 px-4 rounded-md hover:bg-succession-bg-secondary focus:outline-none focus:ring-2 focus:ring-succession-border focus:ring-offset-2 transition-colors shadow-sm"
          >
            {intl.formatMessage({ id: 'succession.search.sidebar.reset' })}
          </button>
        </div>

        {hasAISearchFeature && (
          <button
            type="button"
            onClick={() => {
              onAIModeChange(true);
              onSearchChange('aiMode', true);
            }}
            className="w-full text-succession-text-primary py-2 px-4 bg-succession-bg-tertiary rounded-md hover:bg-succession-bg-secondary focus:outline-none border border-succession-border mt-4 transition-colors"
          >
            {intl.formatMessage({ id: 'succession.search.sidebar.switch_to_ai' })}
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="bg-succession-bg-primary rounded-lg shadow-md p-5 overflow-hidden">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Render AI or traditional search based on mode */}
        {searchParams.aiMode ? renderAISearch() : renderTraditionalSearch()}
      </form>
      
      {/* Add styling for loading animation */}
      <style jsx="true">{`
        .toggle-checkbox:checked {
          transform: translateX(100%);
          border-color: #4f46e5;
        }
        .toggle-label {
          transition: background-color 0.3s ease;
        }
        
        @keyframes loading {
          0%, 80%, 100% { opacity: 0; }
          40% { opacity: 1; }
        }
        
        .loading-dots .dot {
          animation: loading 1.4s infinite ease-in-out both;
        }
        
        .loading-dots .dot1 {
          animation-delay: -0.32s;
        }
        
        .loading-dots .dot2 {
          animation-delay: -0.16s;
        }
        
        .loading-dots .dot3 {
          animation-delay: 0s;
        }
      `}</style>
    </div>
  );
};

export default SearchSidebar; 